import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Send, Eye, Paperclip, X, Loader2, AlertCircle, CheckCircle,
  FileText, Trash2, Upload, Tag, Sparkles
} from 'lucide-react';
import RichTextEditor from '../../components/email/RichTextEditor';
import MergeFieldPicker from '../../components/email/MergeFieldPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTEXTS = [
  { value: 'general', label: 'General', desc: 'AMC + dates only. No investor data.' },
  { value: 'investor_account', label: 'Investor Account', desc: 'One investor + linked contact + fund context.' },
  { value: 'drawdown_investor', label: 'Drawdown', desc: 'Full drawdown + investor + fund context.' },
];

const EmailTemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [template, setTemplate] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    context_type: 'drawdown_investor',
    // Section tags — controls which section dropdowns (Emails, Reminders,
    // Acknowledgments, Drawdown Notice, SOA Delivery, Investor Onboarding)
    // this template appears in at send time. Empty list = hidden everywhere.
    sections: [],
    subject: '',
    body_html: '',
    dynamic_attachments: [],
  });

  // Options fetched from GET /api/email-templates/section-catalog. Keeps
  // labels + values in sync with the backend `TemplateSection` enum.
  const [sectionOptions, setSectionOptions] = useState([]);
  // Which sections are valid for each context — drives the picker so
  // Drawdown context doesn't offer "SOA Delivery" (etc.). Mirrors
  // `TemplateSection.sections_for_context` on the backend.
  const [contextSections, setContextSections] = useState({});

  const [fieldGroups, setFieldGroups] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const subjectInputRef = useRef(null);
  const insertIntoBodyRef = useRef(null);
  const fileInputRef = useRef(null);
  // 'subject' | 'body' — where the next merge-field insert goes
  const [activeTarget, setActiveTarget] = useState('body');

  useEffect(() => {
    if (isEdit) fetchTemplate();
    else setLoading(false);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    // One-shot fetch — enum options don't change per-context.
    (async () => {
      try {
        const { data } = await axios.get(`${API}/email-templates/section-catalog`);
        setSectionOptions(data.sections || []);
        setContextSections(data.context_sections || {});
      } catch { /* fall back to empty; form will still submit an empty sections list */ }
    })();
  }, []);

  // When the context changes, silently drop any selected sections that
  // aren't valid for the new context. Prevents saving a mismatched pair
  // and keeps the checkbox state honest.
  useEffect(() => {
    const allowed = contextSections[form.context_type];
    if (!allowed || !form.sections?.length) return;
    const allowedSet = new Set(allowed);
    const filtered = form.sections.filter((s) => allowedSet.has(s));
    if (filtered.length !== form.sections.length) {
      setForm((f) => ({ ...f, sections: filtered }));
    }
    // eslint-disable-next-line
  }, [form.context_type, contextSections]);

  useEffect(() => {
    fetchFieldCatalog(form.context_type);
    fetchDynamicAttachmentOptions(form.context_type);
    // eslint-disable-next-line
  }, [form.context_type]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email-templates/${id}`);
      setTemplate(data);
      setForm({
        name: data.name || '',
        description: data.description || '',
        context_type: data.context_type || 'drawdown_investor',
        sections: data.sections || [],
        subject: data.subject || '',
        body_html: data.body_html || '',
        dynamic_attachments: data.dynamic_attachments || [],
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load template');
    } finally { setLoading(false); }
  };

  const fetchFieldCatalog = async (ctx) => {
    try {
      const { data } = await axios.get(`${API}/email-templates/field-catalog?context_type=${ctx}`);
      setFieldGroups(data.groups || []);
    } catch { /* ignore */ }
  };
  const fetchDynamicAttachmentOptions = async (ctx) => {
    try {
      const { data } = await axios.get(`${API}/email-templates/dynamic-attachment-options?context_type=${ctx}`);
      setDynamicOptions(data.options || []);
      // Drop selections that are no longer valid for the chosen context
      setForm(f => ({
        ...f,
        dynamic_attachments: (f.dynamic_attachments || []).filter(v => (data.options || []).some(o => o.value === v)),
      }));
    } catch { setDynamicOptions([]); }
  };

  const handleInsert = (token) => {
    if (activeTarget === 'subject') {
      const inp = subjectInputRef.current;
      if (!inp) {
        setForm(f => ({ ...f, subject: f.subject + token }));
        return;
      }
      const start = inp.selectionStart ?? form.subject.length;
      const end = inp.selectionEnd ?? form.subject.length;
      const next = form.subject.slice(0, start) + token + form.subject.slice(end);
      setForm(f => ({ ...f, subject: next }));
      setTimeout(() => {
        inp.focus();
        const pos = start + token.length;
        inp.setSelectionRange(pos, pos);
      }, 0);
    } else {
      insertIntoBodyRef.current?.(token);
    }
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    context_type: form.context_type,
    sections: form.sections,
    subject: form.subject,
    body_html: form.body_html,
    dynamic_attachments: form.dynamic_attachments,
  });

  const handleSave = async (e) => {
    e?.preventDefault();
    setError(''); setSuccess('');
    if (!form.name.trim() || !form.subject.trim()) {
      setError('Name and Subject are required.');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const r = await axios.put(`${API}/email-templates/${id}`, buildPayload());
        saved = r.data;
      } else {
        const r = await axios.post(`${API}/email-templates`, buildPayload());
        saved = r.data;
        navigate(`/email-templates/${saved.id}/edit`, { replace: true });
      }
      setTemplate(saved);
      setSuccess(saved.status === 'draft' && template && template.status !== 'draft'
        ? 'Saved. Status reverted to Draft — please re-submit for approval.'
        : 'Saved successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSubmitForApproval = async () => {
    if (!template) return;
    if (!window.confirm('Submit this template for Admin approval?')) return;
    try {
      await axios.post(`${API}/email-templates/${template.id}/submit`);
      await fetchTemplate();
      setSuccess('Submitted for Admin approval.');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to submit'); }
  };

  const handlePreview = async () => {
    setPreview({ loading: true });
    try {
      // If template doesn't exist yet, save it first
      let tplId = template?.id;
      if (!tplId) {
        await handleSave();
        tplId = template?.id;
        if (!tplId) { setPreview(null); return; }
      }
      const { data } = await axios.post(`${API}/email-templates/${tplId}/preview`);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(err.response?.data?.detail || 'Failed to render preview');
    }
  };

  const handleAttachmentUpload = async (file) => {
    if (!template) {
      setUploadError('Save the template first before adding attachments.');
      return;
    }
    if (!file) return;
    setUploadingAtt(true); setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/email-templates/${template.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchTemplate();
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload');
    } finally { setUploadingAtt(false); }
  };

  const handleAttachmentDelete = async (attId) => {
    if (!template) return;
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await axios.delete(`${API}/email-templates/${template.id}/attachments/${attId}`);
      await fetchTemplate();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to remove'); }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={32} /></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Link to="/email-templates" className="inline-flex items-center text-sm text-gray-600 hover:text-blue-600 mb-3">
        <ArrowLeft size={16} className="mr-1" /> Back to Templates
      </Link>

      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center" data-testid="tpl-form-title">
            <FileText className="mr-3 text-blue-600" size={24} />
            {isEdit ? 'Edit Email Template' : 'New Email Template'}
          </h1>
          {template && (
            <p className="text-sm text-gray-500 mt-1">
              Status: <span className="font-semibold capitalize">{template.status}</span>
              {template.status === 'rejected' && template.rejection_reason && (
                <span className="ml-2 text-red-600 italic">— {template.rejection_reason}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handlePreview} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium flex items-center" data-testid="tpl-preview-btn">
            <Eye size={16} className="mr-1.5" /> Preview
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center disabled:opacity-50" data-testid="tpl-save-btn">
            {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Save size={16} className="mr-1.5" />}
            {isEdit ? 'Save Changes' : 'Create Template'}
          </button>
          {template && (template.status === 'draft' || template.status === 'rejected') && (
            <button onClick={handleSubmitForApproval} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center" data-testid="tpl-submit-for-approval">
              <Send size={16} className="mr-1.5" /> Submit for Approval
            </button>
          )}
        </div>
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center" data-testid="tpl-form-success">
          <CheckCircle size={16} className="text-green-600 mr-2" /> {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-start" data-testid="tpl-form-error">
          <AlertCircle size={16} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" /> <span className="whitespace-pre-wrap">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Template fields */}
        <div className="lg:col-span-2 space-y-5 bg-white border border-gray-200 rounded-lg p-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
            <input
              type="text" required
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. Drawdown Notice — Standard"
              data-testid="tpl-input-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Short note for other team members (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Context *</label>
            <div className="grid grid-cols-3 gap-3">
              {CONTEXTS.map(c => (
                <button
                  type="button"
                  key={c.value}
                  onClick={() => setForm({ ...form, context_type: c.value })}
                  className={`text-left p-3 border rounded-lg transition-colors ${
                    form.context_type === c.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  data-testid={`tpl-ctx-${c.value}`}
                >
                  <p className={`text-sm font-semibold ${form.context_type === c.value ? 'text-blue-700' : 'text-gray-800'}`}>{c.label}</p>
                  <p className="text-[11px] text-gray-500 mt-1">{c.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sections <span className="text-gray-400 font-normal">(select where this template can be used)</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="tpl-sections-picker">
              {(() => {
                // Only render sections valid for the currently-selected context.
                // Falls back to the full list when the mapping hasn't loaded yet
                // (never blocks the user from picking).
                const allowed = contextSections[form.context_type];
                const visibleOpts = allowed
                  ? sectionOptions.filter((s) => allowed.includes(s.value))
                  : sectionOptions;
                return visibleOpts;
              })().map((s) => {
                const checked = (form.sections || []).includes(s.value);
                return (
                  <label
                    key={s.value}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                      checked ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                    data-testid={`tpl-section-opt-${s.value}`}
                  >
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={checked}
                      onChange={(e) => {
                        setForm((f) => {
                          const cur = new Set(f.sections || []);
                          if (e.target.checked) cur.add(s.value); else cur.delete(s.value);
                          return { ...f, sections: Array.from(cur) };
                        });
                      }}
                    />
                    <span className="text-sm font-medium">{s.label}</span>
                  </label>
                );
              })}
            </div>
            {(!form.sections || form.sections.length === 0) && (
              <p className="text-[11px] text-amber-600 mt-1.5">
                Tag at least one section — untagged templates are hidden from every section&apos;s dropdown.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Subject *</label>
              <div onClick={() => setActiveTarget('subject')}>
                <MergeFieldPicker groups={fieldGroups} onPick={handleInsert} label="Insert into Subject" />
              </div>
            </div>
            <input
              type="text" required
              ref={subjectInputRef}
              value={form.subject}
              onFocus={() => setActiveTarget('subject')}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              placeholder="e.g. Drawdown {{drawdown_number}} — {{account_name}}"
              data-testid="tpl-input-subject"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Body *</label>
              <div onClick={() => setActiveTarget('body')}>
                <MergeFieldPicker groups={fieldGroups} onPick={handleInsert} label="Insert into Body" />
              </div>
            </div>
            <RichTextEditor
              value={form.body_html}
              onChange={(html) => setForm(f => ({ ...f, body_html: html }))}
              placeholder="Compose the email body. Use the merge-field picker to insert {{...}} tokens."
              registerInsertHandler={(fn) => { insertIntoBodyRef.current = fn; }}
            />
            <p className="text-xs text-gray-500 mt-1 flex items-center">
              <Sparkles size={12} className="mr-1 text-amber-500" />
              Tip: surround merge fields with regular text — e.g. <span className="font-mono ml-1">Dear &#123;&#123;investor_full_name&#125;&#125;,</span>
            </p>
          </div>
        </div>

        {/* Right: Attachments */}
        <div className="space-y-5">
          {/* Dynamic attachments */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center mb-2"><Sparkles size={16} className="mr-2 text-purple-600" /> Auto-resolved Attachments</h3>
            <p className="text-xs text-gray-500 mb-3">These are fetched at send time, per recipient.</p>
            {dynamicOptions.length === 0 && (
              <p className="text-xs text-gray-400 italic">None available for "{form.context_type}" context.</p>
            )}
            <div className="space-y-2">
              {dynamicOptions.map(opt => (
                <label key={opt.value} className="flex items-start cursor-pointer p-2 border border-gray-100 rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={form.dynamic_attachments.includes(opt.value)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...form.dynamic_attachments, opt.value]
                        : form.dynamic_attachments.filter(v => v !== opt.value);
                      setForm({ ...form, dynamic_attachments: next });
                    }}
                    className="h-4 w-4 mt-0.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    data-testid={`tpl-dyn-${opt.value}`}
                  />
                  <span className="ml-2 text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Static attachments */}
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center mb-2"><Paperclip size={16} className="mr-2 text-blue-600" /> Static Attachments</h3>
            <p className="text-xs text-gray-500 mb-3">Same file is sent to every recipient (e.g. newsletters).</p>
            {!template && <p className="text-xs italic text-amber-700 mb-2">Save the template first to enable file uploads.</p>}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleAttachmentUpload(f);
                e.target.value = '';
              }}
              data-testid="tpl-static-att-file"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!template || uploadingAtt}
              className="w-full px-3 py-2 border border-dashed border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="tpl-static-att-upload-btn"
            >
              {uploadingAtt ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
              Upload file (max 25 MB)
            </button>
            {uploadError && <p className="text-xs text-red-600 mt-2">{uploadError}</p>}
            <ul className="mt-3 space-y-2">
              {(template?.static_attachments || []).map(att => (
                <li key={att.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs" data-testid={`tpl-static-att-${att.id}`}>
                  <div className="flex items-center min-w-0">
                    <Paperclip size={12} className="text-gray-400 mr-1.5 flex-shrink-0" />
                    <span className="truncate" title={att.filename}>{att.filename}</span>
                    <span className="text-gray-400 ml-2 flex-shrink-0">{Math.round((att.size_bytes || 0) / 1024)} KB</span>
                  </div>
                  <button onClick={() => handleAttachmentDelete(att.id)} className="text-red-500 hover:bg-red-50 p-1 rounded ml-2 flex-shrink-0" title="Remove">
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
              {(template?.static_attachments || []).length === 0 && template && (
                <li className="text-xs text-gray-400 italic">No attachments.</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Preview drawer */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center"><Eye size={18} className="mr-2" /> Email Preview (sample data)</h3>
              <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {preview.loading ? (
                <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-blue-600" size={28} /></div>
              ) : (
                <>
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Subject</p>
                    <p className="text-sm font-semibold text-gray-900" data-testid="tpl-preview-subject">{preview.subject}</p>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Body</p>
                    {/* Preview lives in the parent DOM where Tailwind's
                       Preflight has zeroed out list-style. Use the
                       `email-editor` class so the same explicit list /
                       heading / quote CSS from RichTextEditor renders
                       bullets, numbers, and other formatting the author
                       just applied. */}
                    <div className="email-editor max-w-none border border-gray-100 rounded p-3 bg-gray-50" data-testid="tpl-preview-body" dangerouslySetInnerHTML={{ __html: preview.body_html }} />
                  </div>
                  {preview.unresolved_keys && preview.unresolved_keys.length > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                      <strong>Unresolved keys:</strong> {preview.unresolved_keys.map(k => <span key={k} className="font-mono mr-1">{`{{${k}}}`}</span>)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateForm;
